import React, { useState, useMemo, useEffect } from 'react'
import { Modal, Form, Select, Alert, Button, Space, Typography, Divider, Checkbox } from 'antd'
import { WarningOutlined, InfoCircleOutlined } from '@ant-design/icons'
import IntlMessages from '../../util/IntlMessages'
import { useIntl } from 'react-intl'

import Api from '@vacationtracker/shared/services/api'
import { graphqlRequest } from '../../services/tanstack-query/graphqlClient'
import { useAppSelector } from '../../store/hooks'
import { selectAuthUserSlice } from '../../store/auth-user-slice'
import { track } from '../../services/analytics/analytics'
import { getAnalyticsProperties } from '../../util/analytics-helpers'

import type {
  IPlanDowngradeModalProps,
  IEntitySelections,
  IEntityLimits,
  IEntitySourceData
} from './types'

import { PLAN_LIMITS } from './types'
import { IGetLeaveTypeListLimits } from '../../types/company'
import { IGetCompanyBillingTeamList } from '../../types/teams'

import './styles.less'
import { wait } from '@vacationtracker/shared/functions/wait'
import { selectAuthCompanySlice } from '../../store/auth-company-slice'


const { Text } = Typography

const DELETION_EVENT_DELAY = 1000 // 1 second between each deletion event

export const PlanDowngradeModal: React.FC<IPlanDowngradeModalProps> = ({
  visible,
  onCancel,
  onSubmit,
  sourceData,
  downgradeType,
  targetPlan,
  currentPeriod,
  endDatePeriod,
  numberOfActiveUsers = 0,
  isTrialPeriod = false,
  source,
  analyticsOverrides,
}) => {
  const intl = useIntl()
  const { authUser } = useAppSelector(selectAuthUserSlice)
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const [form] = Form.useForm()
  const [isProcessing, setIsProcessing] = useState(false)

  useEffect(() => {
    if (visible) {
      track('DOWNGRADE_REQUIREMENTS_MODAL_VIEWED', {
        ...getAnalyticsProperties(analyticsOverrides),
        plan: targetPlan,
        source: source || 'billing_page',
      })
    }
  }, [visible, targetPlan, source, analyticsOverrides])

  const limits: IEntityLimits = PLAN_LIMITS[targetPlan]
  const isSingleSelect = targetPlan === 'Freemium'

  // Helper: Get initial department selection
  const getInitialDepartmentSelection = (): string[] => {
    // For Core: pre-select default department if it exists
    if (targetPlan === 'Core') {
      const defaultDept = sourceData.teams.find((team) => team.isDefault)
      if (defaultDept) {
        return [defaultDept.id]
      }
    }
    // Auto-select if only one department
    return sourceData.teams.length === 1 ? [sourceData.teams[0].id] : []
  }

  // Helper: Get initial location selection
  const getInitialLocationSelection = (): string[] => {
    // For Core: pre-select default location if it exists
    if (targetPlan === 'Core') {
      const defaultLocation = sourceData.locations.find((location) => location.isDefault)
      if (defaultLocation) {
        return [defaultLocation.id]
      }
    }
    // Auto-select if only one location
    return sourceData.locations.length === 1 ? [sourceData.locations[0].id] : []
  }

  const [formValues, setFormValues] = useState<IEntitySelections>({
    selectedDepartmentIds: getInitialDepartmentSelection(),
    selectedLocationIds: getInitialLocationSelection(),
    selectedLeaveTypeIds: [],
  })

  // Sort departments: default first, then alphabetically
  const sortedDepartments = useMemo(() => {
    return [...sourceData.teams].sort((a, b) => {
      // Default department comes first
      if (a.isDefault) return -1
      if (b.isDefault) return 1
      // Then sort alphabetically by name
      return a.name.localeCompare(b.name)
    })
  }, [sourceData.teams])

  // Sort locations: default first, then alphabetically
  const sortedLocations = useMemo(() => {
    return [...sourceData.locations].sort((a, b) => {
      // Default location comes first
      if (a.isDefault) return -1
      if (b.isDefault) return 1
      // Then sort alphabetically by name
      return a.name.localeCompare(b.name)
    })
  }, [sourceData.locations])

  // Filter active leave types from selected locations' leave policies
  const activeLeaveTypes = useMemo(() => {
    // Determine which locations to use for leave type calculation
    let locationIdsToUse: string[]
    
    if (!formValues.selectedLocationIds?.length) {
      // If no locations selected AND locations don't exceed limit, use all locations
      if (sourceData.locations.length <= limits.locations) {
        locationIdsToUse = sourceData.locations.map((loc) => loc.id)
      } else {
        // No selection made but selection is required - return empty
        return []
      }
    } else {
      locationIdsToUse = formValues.selectedLocationIds
    }

    // Get selected locations
    const selectedLocations = sourceData.locations.filter((location) =>
      locationIdsToUse.includes(location.id)
    )

    // Collect unique leave types from all selected locations (union)
    const leaveTypeMap = new Map<string, IGetLeaveTypeListLimits>()

    selectedLocations.forEach((location) => {
      location.leavePolicies?.forEach((leavePolicy) => {
        // Filter where both leave policy and leave type are active
        if (
          leavePolicy.isActive === true &&
          leavePolicy.leaveType.isActive === true &&
          !leavePolicy.leaveType.deleted
        ) {
          // Use leaveType.id as key to deduplicate
          if (!leaveTypeMap.has(leavePolicy.leaveType.id)) {
            leaveTypeMap.set(leavePolicy.leaveType.id, {
              id: leavePolicy.leaveType.id,
              name: leavePolicy.leaveType.name,
              isActive: leavePolicy.leaveType.isActive,
              position: leavePolicy.leaveType.position,
            })
          }
        }
      })
    })

    // Sort by position before returning
    return Array.from(leaveTypeMap.values()).sort((a, b) => a.position - b.position)
  }, [sourceData.locations, formValues.selectedLocationIds, limits.locations])

  // Clear invalid leave type selections when location changes
  useEffect(() => {
    const currentLeaveTypeIds = formValues.selectedLeaveTypeIds || []
    
    // If there's exactly 1 leave type, auto-select it
    if (activeLeaveTypes.length === 1 && formValues.selectedLocationIds?.length) {
      const singleLeaveTypeId = activeLeaveTypes[0].id
      const expectedValue = isSingleSelect ? singleLeaveTypeId : [singleLeaveTypeId]
      
      // Only update if not already set (check if current value matches)
      const isAlreadySet = Array.isArray(currentLeaveTypeIds) 
        ? currentLeaveTypeIds.length === 1 && currentLeaveTypeIds[0] === singleLeaveTypeId
        : false
      
      if (!isAlreadySet) {
        form.setFieldValue('selectedLeaveTypeIds', expectedValue)
        // Also update formValues state to trigger deletion summary re-calculation
        setFormValues((prev) => ({
          ...prev,
          selectedLeaveTypeIds: [singleLeaveTypeId],
        }))
      }
    } else if (currentLeaveTypeIds.length > 0 && formValues.selectedLocationIds?.length) {
      // Get available leave type IDs from the current activeLeaveTypes
      const availableLeaveTypeIds = new Set(activeLeaveTypes.map(lt => lt.id))
      
      // Filter out invalid leave type selections
      const validLeaveTypeIds = currentLeaveTypeIds.filter((id: string) =>
        availableLeaveTypeIds.has(id)
      )
      
      // If some leave types are no longer valid, update the form
      if (validLeaveTypeIds.length !== currentLeaveTypeIds.length) {
        form.setFieldValue('selectedLeaveTypeIds', validLeaveTypeIds)
        // Also update formValues state
        setFormValues((prev) => ({
          ...prev,
          selectedLeaveTypeIds: validLeaveTypeIds,
        }))
      }
    }
  }, [formValues.selectedLocationIds, activeLeaveTypes, formValues.selectedLeaveTypeIds, form, isSingleSelect])

  const handleSubmit = async () => {
    // Validate form
    const values = await form.validateFields()

    track('DOWNGRADE_WITH_DEFAULTS_CONFIRMED', {
      ...getAnalyticsProperties(analyticsOverrides),
      plan: targetPlan,
      source: source || 'billing_page',
    })

    setIsProcessing(true)

    try {
      const summary = getDeletionSummary(formValues, sourceData, limits)

      // Step 1: Send deletion/disable events with timeouts between each
      await sendDeletionEvents(summary)

      // Step 2: Return control to parent component
      // Parent waits 6s then sends BILLING_UPDATED event
      await wait(6000)
      await onSubmit(values as IEntitySelections)

      // Reset state
      form.resetFields()
    } catch (error) {
      console.error('Error during downgrade process:', error)
    } finally {
      setIsProcessing(false)
    }
  }

  const sendDeletionEvents = async (summary: ReturnType<typeof getDeletionSummary>) => {
    // For Freemium, set remaining team and location as default BEFORE deleting entities
    // This ensures that when TEAM_DELETED and LOCATION_DELETED handlers move users to default,
    // they move them to the correct (remaining) entities, not the old defaults being deleted
    if (targetPlan === 'Freemium') {
      // Set remaining team as default FIRST
      if (summary.departments.length > 0) {
        const selectedDeptIds = formValues.selectedDepartmentIds || []
        const remainingTeam = sourceData.teams.find((team) => selectedDeptIds.includes(team.id))

        if (remainingTeam && !remainingTeam.isDefault) {
          // Set the remaining team as default before any deletions
          // For Freemium:
          // 1. Remove parentId to convert subdepartment to top-level department (allows parent deletion)
          // 2. Set single-level approval with only the current admin as approver (Freemium doesn't support multi-level or multiple approvers)
          await Api.post('/core/event', {
            eventType: 'TEAM_UPDATED',
            eventGroup: 'TEAM',
            teamId: remainingTeam.id,
            name: remainingTeam.name,
            isDefault: true,
            users: [], // Empty array since we're not moving users, just setting default
            approvers: [
              {
                userId: authUser.id, // Current logged-in admin becomes the only approver
                approverLevels: ['FIRST'], // Single-level approval only (Freemium requirement)
              },
            ],
            version: 2,
            // Don't include parentId - this removes the parent relationship, allowing parent to be deleted
            // Don't include watchersToNotify - Freemium doesn't support this feature
          })
          await new Promise((resolve) => setTimeout(resolve, DELETION_EVENT_DELAY))
        }
      }

      // Set remaining location as default FIRST
      if (summary.locations.length > 0) {
        const selectedLocationIds = formValues.selectedLocationIds || []
        const remainingLocationShort = sourceData.locations.find((location) => selectedLocationIds.includes(location.id))

        if (remainingLocationShort && !remainingLocationShort.isDefault) {
          // Fetch full location data to get all required fields
          const locationResponse = await graphqlRequest<{ getLocation: {
            id: string
            name: string
            yearStartMonth: number
            yearStartDay: number
            firstDayOfWeek: number
            resetQuotas: string
            timezone: string
            users: { id: string }[]
          }}>(
            `query GetLocationById($id: String!) {
              getLocation(id: $id) {
                id
                name
                yearStartMonth
                yearStartDay
                firstDayOfWeek
                resetQuotas
                timezone
                users { id }
              }
            }`,
            { id: remainingLocationShort.id }
          )

          const locationData = locationResponse.getLocation
          if (locationData) {
            // Set the remaining location as default before any deletions
            await Api.post('/core/event', {
              eventType: 'LOCATION_UPDATED',
              eventGroup: 'LOCATION',
              locationId: locationData.id,
              name: locationData.name,
              yearStartMonth: locationData.yearStartMonth,
              yearStartDay: locationData.yearStartDay,
              isDefault: true,
              firstDayOfWeek: locationData.firstDayOfWeek,
              resetQuotas: locationData.resetQuotas,
              timezone: locationData.timezone,
              users: [], // Empty array since we're not moving users, just setting default
            })
            await new Promise((resolve) => setTimeout(resolve, DELETION_EVENT_DELAY))
          }
        }
      }
    }

    // Now prepare deletion events - users will be moved to the NEW defaults we just set
    const events: Array<() => Promise<unknown>> = []

    // Prepare TEAM_DELETED events
    summary.departments.forEach((dept) => {
      events.push(() =>
        Api.post('/core/event', {
          eventType: 'TEAM_DELETED',
          eventGroup: 'TEAM',
          teamId: dept.id,
        })
      )
    })

    // Prepare LOCATION_DELETED events
    summary.locations.forEach((location) => {
      events.push(() =>
        Api.post('/core/event', {
          eventType: 'LOCATION_DELETED',
          eventGroup: 'LOCATION',
          locationId: location.id,
        })
      )
    })

    // Prepare LEAVE_TYPE_UPDATED events (set isActive: false)
    summary.leaveTypes.forEach((leaveType) => {
      events.push(() =>
        Api.post('/core/event', {
          eventType: 'LEAVE_TYPE_UPDATED',
          eventGroup: 'LEAVE_TYPE',
          leaveTypeId: leaveType.id,
          name: leaveType.name,
          color: leaveType.color || '#000000',
          isActive: false,
        })
      )
    })

    // Execute deletion events with delays
    for (let i = 0; i < events.length; i++) {
      await events[i]()
      if (i < events.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, DELETION_EVENT_DELAY))
      }
    }
  }

  const renderSummaryPreview = () => {
    const summary = getDeletionSummary(formValues, sourceData, limits)

    const needsDepartmentSelection = sourceData.teams.length > limits.departments
    const needsLocationSelection = sourceData.locations.length > limits.locations
    const needsLeaveTypeSelection = activeLeaveTypes.length > limits.leaveTypes

    // For multi-select (Core), require exact limit to be selected to show the deletion summary
    // For single-select (Freemium), just check if any selection was made
    const hasDepartmentSelection = !needsDepartmentSelection ||
      (isSingleSelect
        ? (formValues.selectedDepartmentIds?.length || 0) > 0
        : (formValues.selectedDepartmentIds?.length || 0) === limits.departments)

    const hasLocationSelection = !needsLocationSelection ||
      (isSingleSelect
        ? (formValues.selectedLocationIds?.length || 0) > 0
        : (formValues.selectedLocationIds?.length || 0) === limits.locations)

    const hasLeaveTypeSelection = !needsLeaveTypeSelection ||
      (isSingleSelect
        ? (formValues.selectedLeaveTypeIds?.length || 0) > 0
        : (formValues.selectedLeaveTypeIds?.length || 0) === limits.leaveTypes)

    const allRequiredSelectionsComplete = hasDepartmentSelection && hasLocationSelection && hasLeaveTypeSelection

    if (!allRequiredSelectionsComplete) {
      return null
    }

    const hasDeletions =
      summary.departments.length > 0 ||
      summary.locations.length > 0 ||
      summary.leaveTypes.length > 0

    if (!hasDeletions) {
      return (
        <Alert
          type="info"
          icon={<InfoCircleOutlined />}
          message={
            <IntlMessages
              id="app.planDowngrade.planLimitations"
              values={{ plan: targetPlan }}
            />
          }
          description={
            <>
              {/* Show plan limitations */}
              <div>
                <IntlMessages
                  id={targetPlan === 'Core'
                    ? 'app.planDowngrade.planLimitationsCore'
                    : 'app.planDowngrade.planLimitationsFreemium'}
                  values={{
                    departments: limits.departments,
                    locations: limits.locations,
                    leaveTypes: limits.leaveTypes,
                    strong: (...chunks: React.ReactNode[]) => <strong>{chunks}</strong>,
                  }}
                />
              </div>
              {/* Show current setup */}
              <div style={{ marginTop: 8 }}>
                <IntlMessages
                  id="app.planDowngrade.planLimitationsYourSetup"
                  values={{
                    currentDepartments: sourceData.teams.length,
                    currentLocations: sourceData.locations.length,
                    currentLeaveTypes: activeLeaveTypes.length,
                  }}
                />
              </div>
              <div style={{ marginTop: 8 }}>
                <IntlMessages id="app.planDowngrade.requirementsMet" />
              </div>
            </>
          }
          style={{ marginTop: 16 }}
        />
      )
    }

    return (
      <Alert
        type="warning"
        icon={<WarningOutlined />}
        message={
          <IntlMessages id="app.planDowngrade.deletionSummary" />
        }
        description={
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            {summary.departments.length > 0 && (
              <div>
                <Text strong>
                  <IntlMessages
                    id="app.planDowngrade.departmentsToDelete"
                    values={{ count: summary.departments.length }}
                  />
                  :{' '}
                </Text>
                <Text>{summary.departments.map((dept) => dept.name).join(', ')}</Text>
              </div>
            )}
            {summary.locations.length > 0 && (
              <div>
                <Text strong>
                  <IntlMessages
                    id="app.planDowngrade.locationsToDelete"
                    values={{ count: summary.locations.length }}
                  />
                  :{' '}
                </Text>
                <Text>{summary.locations.map((location) => location.name).join(', ')}</Text>
              </div>
            )}
            {summary.leaveTypes.length > 0 && (
              <div>
                <Text strong>
                  <IntlMessages
                    id="app.planDowngrade.leaveTypesToDisable"
                    values={{ count: summary.leaveTypes.length }}
                  />
                  :{' '}
                </Text>
                <Text>{summary.leaveTypes.map((leaveType) => leaveType.name).join(', ')}</Text>
              </div>
            )}
          </Space>
        }
        style={{ marginTop: 16 }}
      />
    )
  }

  const renderContent = () => {
    // Determine if any entity selections are needed
    const needsEntitySelection =
      sourceData.teams.length > limits.departments ||
      sourceData.locations.length > limits.locations ||
      activeLeaveTypes.length > limits.leaveTypes

    return (
      <div className="plan-downgrade-selection-step">
        {/* Only show "Prepare for X Plan" alert if entity selections are needed */}
        {needsEntitySelection && (
          <Alert
            type="info"
            icon={<InfoCircleOutlined />}
            message={
              <IntlMessages
                id="app.planDowngrade.infoMessage"
                values={{ plan: targetPlan }}
              />
            }
            description={
              <>
                {targetPlan === 'Freemium' && numberOfActiveUsers > 0 && (sourceData.teams.length > limits.departments || sourceData.locations.length > limits.locations) && (
                  <div style={{ marginBottom: 8 }}>
                    <IntlMessages
                      id="app.planDowngrade.usersMigrationInfo"
                      values={{
                        count: numberOfActiveUsers,
                        strong: (...chunks: React.ReactNode[]) => <strong>{chunks}</strong>,
                      }}
                    />
                  </div>
                )}
                {sourceData.teams.length > limits.departments && (
                  <div>
                    <IntlMessages
                      id="app.planDowngrade.infoDescriptionDepartments"
                      values={{
                        currentDepartments: sourceData.teams.length,
                        departments: limits.departments,
                      }}
                    />
                  </div>
                )}
                {sourceData.locations.length > limits.locations && (
                  <div>
                    <IntlMessages
                      id="app.planDowngrade.infoDescriptionLocations"
                      values={{
                        currentLocations: sourceData.locations.length,
                        locations: limits.locations,
                      }}
                    />
                  </div>
                )}
                {activeLeaveTypes.length > limits.leaveTypes && (
                  <div>
                    <IntlMessages
                      id="app.planDowngrade.infoDescriptionLeaveTypes"
                      values={{
                        currentLeaveTypes: activeLeaveTypes.length,
                        leaveTypes: limits.leaveTypes,
                      }}
                    />
                  </div>
                )}
                <div style={{ marginTop: 8 }}>
                  <IntlMessages id="app.planDowngrade.selectWhichToKeep" />
                </div>
              </>
            }
            style={{ marginBottom: 16 }}
          />
        )}

        <Form
          form={form}
          layout="vertical"
          onValuesChange={(changedValues, allValues) => {
            // Normalize single-select values to arrays (for Freemium plan)
            const normalizedValues: IEntitySelections = {
              selectedDepartmentIds: Array.isArray(allValues.selectedDepartmentIds)
                ? allValues.selectedDepartmentIds
                : allValues.selectedDepartmentIds
                  ? [allValues.selectedDepartmentIds]
                  : [],
              selectedLocationIds: Array.isArray(allValues.selectedLocationIds)
                ? allValues.selectedLocationIds
                : allValues.selectedLocationIds
                  ? [allValues.selectedLocationIds]
                  : [],
              selectedLeaveTypeIds: Array.isArray(allValues.selectedLeaveTypeIds)
                ? allValues.selectedLeaveTypeIds
                : allValues.selectedLeaveTypeIds
                  ? [allValues.selectedLeaveTypeIds]
                  : [],
            }
            // Update state to trigger summary preview re-render
            setFormValues(normalizedValues)
          }}
        >
          {/* Department Selection - Only show if exceeds limit */}
          {sourceData.teams.length > limits.departments && (
            <Form.Item
              name="selectedDepartmentIds"
              label={
                <IntlMessages
                  id={
                    isSingleSelect
                      ? 'app.planDowngrade.keepDepartment'
                      : 'app.planDowngrade.keepDepartments'
                  }
                  values={{ max: limits.departments }}
                />
              }
              initialValue={getInitialDepartmentSelection()}
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({ id: 'app.required' }),
                },
                {
                  validator: async (_, value) => {
                    if (targetPlan === 'Core') {
                      const defaultDept = sourceData.teams.find((team) => team.isDefault)
                      if (defaultDept && (!value || !value.includes(defaultDept.id))) {
                        return Promise.reject(new Error(intl.formatMessage({ id: 'app.planDowngrade.defaultDepartmentRequired' })))
                      }
                    }
                    return Promise.resolve()
                  },
                },
              ]}
            >
              <Select
                mode={isSingleSelect ? undefined : 'multiple'}
                placeholder={intl.formatMessage({
                  id: 'app.planDowngrade.selectPlaceholder',
                })}
                disabled={false}
                maxCount={!isSingleSelect ? limits.departments : undefined}
                options={sortedDepartments.map((team) => ({
                  value: team.id,
                  label: team.name,
                  // For Core: disable default department so it can't be removed
                  disabled: targetPlan === 'Core' && team.isDefault,
                }))}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          )}

          {/* Location Selection - Only show if exceeds limit */}
          {sourceData.locations.length > limits.locations && (
            <Form.Item
              name="selectedLocationIds"
              label={
                <IntlMessages
                  id={
                    isSingleSelect
                      ? 'app.planDowngrade.keepLocation'
                      : 'app.planDowngrade.keepLocations'
                  }
                  values={{ max: limits.locations }}
                />
              }
              initialValue={getInitialLocationSelection()}
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({ id: 'app.required' }),
                },
                {
                  validator: async (_, value) => {
                    if (targetPlan === 'Core') {
                      const defaultLocation = sourceData.locations.find((location) => location.isDefault)
                      if (defaultLocation && (!value || !value.includes(defaultLocation.id))) {
                        return Promise.reject(new Error(intl.formatMessage({ id: 'app.planDowngrade.defaultLocationRequired' })))
                      }
                    }
                    return Promise.resolve()
                  },
                },
              ]}
            >
              <Select
                mode={isSingleSelect ? undefined : 'multiple'}
                placeholder={intl.formatMessage({
                  id: 'app.planDowngrade.selectPlaceholder',
                })}
                disabled={false}
                maxCount={!isSingleSelect ? limits.locations : undefined}
                options={sortedLocations.map((location) => ({
                  value: location.id,
                  label: location.name,
                  // For Core: disable default location so it can't be removed
                  disabled: targetPlan === 'Core' && location.isDefault,
                }))}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          )}

          {/* Leave Type Selection - Only show if exceeds limit (Core has unlimited, so never shows) */}
          {targetPlan === 'Freemium' && activeLeaveTypes.length > 0 && (
            <Form.Item
              name="selectedLeaveTypeIds"
              label={
                <IntlMessages
                  id={
                    isSingleSelect
                      ? 'app.planDowngrade.keepLeaveType'
                      : 'app.planDowngrade.keepLeaveTypes'
                  }
                  values={{ max: limits.leaveTypes }}
                />
              }
              initialValue={activeLeaveTypes.length === 1 ? [activeLeaveTypes[0].id] : undefined}
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({ id: 'app.required' }),
                },
              ]}
            >
              <Select
                mode={isSingleSelect ? undefined : 'multiple'}
                placeholder={intl.formatMessage({
                  id: 'app.planDowngrade.selectPlaceholder',
                })}
                disabled={
                  (sourceData.locations.length > limits.locations && !formValues.selectedLocationIds?.length) ||
                  activeLeaveTypes.length === 0 ||
                  activeLeaveTypes.length === 1
                }
                options={activeLeaveTypes.map((leaveType) => ({
                  value: leaveType.id,
                  label: leaveType.name,
                }))}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          )}

          {renderSummaryPreview()}
        </Form>

        <Divider />

        {/* Confirmation Section - Hide "cannot be undone" message for Complete → Core during trial */}
        <Alert
          type="error"
          icon={<WarningOutlined />}
          message={
            downgradeType === 'complete-to-core' && isTrialPeriod
              ? undefined
              : <IntlMessages id="app.planDowngrade.confirmWarning" />
          }
          description={
            <>
              <IntlMessages
                id={
                  downgradeType === 'to-freemium'
                    ? 'app.planDowngrade.confirmWarningFreemium'
                    : 'app.planDowngrade.confirmWarningCore'
                }
              />

              {/* Show billing cycle timing info */}
              {authCompany?.subscriptionStatus && !['canceled', 'trialing'].includes(authCompany.subscriptionStatus) && !!endDatePeriod && !!currentPeriod && (
                <>
                  <br /><br />
                  <IntlMessages
                    id="app.planDowngrade.activationTiming"
                    values={{
                      plan: targetPlan,
                      period: targetPlan === 'Freemium' ? '' : currentPeriod,
                      endDate: endDatePeriod,
                      strong: (...chunks: React.ReactNode[]) => <strong>{chunks}</strong>,
                    }}
                  />
                </>
              )}
            </>
          }
          style={{ marginBottom: 24 }}
        />

        <div style={{ textAlign: 'right' }}>
          <Space>
            <Button onClick={onCancel} disabled={isProcessing}>
              <IntlMessages id="app.cancel" />
            </Button>
            <Button
              type="primary"
              danger
              onClick={() => {
                handleSubmit().catch((error) => {
                  console.error('Error submitting downgrade:', error)
                })
              }}
              loading={isProcessing}
            >
              <IntlMessages id="app.planDowngrade.confirmSubmit" />
            </Button>
          </Space>
        </div>
      </div>
    )
  }

  const handleCancel = () => {
    if (!isProcessing) {
      track('DOWNGRADE_REQUIREMENTS_MODAL_DISMISSED', {
        ...getAnalyticsProperties(analyticsOverrides),
        plan: targetPlan,
        source: source || 'billing_page',
      })
      form.resetFields()
      onCancel()
    }
  }

  return (
    <Modal
      title={
        <IntlMessages
          id="app.planDowngrade.modalTitle"
          values={{ plan: targetPlan }}
        />
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={700}
      maskClosable={!isProcessing}
      closable={!isProcessing}
      className="plan-downgrade-modal"
    >
      {renderContent()}
    </Modal>
  )
}

// Calculate what will be deleted/disabled based on current form values
const getDeletionSummary = (
  values: IEntitySelections,
  sourceData: IEntitySourceData,
  limits: IEntityLimits
): {
  departments: IGetCompanyBillingTeamList[]
  locations: {
    id: string
    name: string
  }[]
  leaveTypes: IGetLeaveTypeListLimits[]
} => {
  const selectedDeptIds = values.selectedDepartmentIds || []
  const selectedLocationIds = values.selectedLocationIds || []
  const selectedLeaveTypeIds = values.selectedLeaveTypeIds || []

  // Only calculate deletions for entities that exceed limits
  let departmentsToDelete = sourceData.teams.length > limits.departments
    ? sourceData.teams.filter((t) => !selectedDeptIds.includes(t.id))
    : []

  // Sort departments: deepest children (most ancestors) must be deleted before parents
  // Uses depth-based sorting to handle multi-level hierarchies correctly
  if (departmentsToDelete.length > 0) {
    // Helper to compute depth of a department in the hierarchy
    const getDeptDepth = (dept: IGetCompanyBillingTeamList, allDepts: IGetCompanyBillingTeamList[]): number => {
      let depth = 0
      let current = dept
      while (current.parentId) {
        const parent = allDepts.find(d => d.id === current.parentId)
        if (!parent) break
        depth += 1
        current = parent
      }
      return depth
    }

    departmentsToDelete = departmentsToDelete.sort((a, b) => {
      // Deeper departments (more ancestors) come first
      const depthA = getDeptDepth(a, sourceData.teams)
      const depthB = getDeptDepth(b, sourceData.teams)
      return depthB - depthA
    })
  }

  const locationsToDelete = sourceData.locations.length > limits.locations
    ? sourceData.locations.filter((l) => !selectedLocationIds.includes(l.id))
    : []

  const leaveTypesToDisable = sourceData.leaveTypes.length > limits.leaveTypes
    ? sourceData.leaveTypes
      .filter((lt) => !selectedLeaveTypeIds.includes(lt.id))
      .sort((a, b) => a.position - b.position)
    : []

  return {
    departments: departmentsToDelete,
    locations: locationsToDelete,
    leaveTypes: leaveTypesToDisable,
  }
}

export default PlanDowngradeModal
